import { userRoles, operations, mapComponentToResource } from './constants';

const ORG_ADMIN_DASHBOARD_RESOURCES = [
  mapComponentToResource.Dashboard,
  mapComponentToResource.AccountDashboard
];

const ORG_ADMIN_DASHBOARD_OPERATIONS = [
  operations.Create,
  operations.Read,
  operations.Update
];

export function isPetasenseAdmin(user) {
  if (user && user.roles && user.roles.includes(userRoles.PetasenseAdmin)) return true;
  return false;
}

export function isPetasenseViewer(user) {
  if (user && user.roles && user.roles.includes(userRoles.PetasenseViewer)) return true;
  return false;
}

export function isPartnerAdmin(user) {
  // accounts is guarded separately: it is absent while the user payload is still
  // loading, and this now runs during render, where a throw blanks the page.
  if (user && user.roles && user.roles.includes(userRoles.PartnerAdmin) && user.accounts &&
    user.accounts.map(acc => acc.id).includes(user.account_id)) return true;
  return false;
}

export function canManageOrganisations(user) {
  return isPetasenseAdmin(user) || isPartnerAdmin(user);
}

// Partner admins manage subscriptions for their own customers. This is a UI
// hint only: the user payload carries no partner_id for the organisation being
// edited, so scope is decided by the API, which re-checks partner_id on every
// site and organisation write.
export function canEditSubscription(user) {
  return isPetasenseAdmin(user) || isPartnerAdmin(user);
}

export function isAnalyst(user) {
  if (user && user.roles && user.roles.includes(userRoles.Analyst)) return true;
  return false;
}

export function isAdmin(user) {
  if (user && user.roles && user.roles.includes(userRoles.Admin)) return true;
  return false;
}

// Creating machine sub-types is reserved for analysts and Petasense admins.
export function canManageMachineSubTypes(user) {
  return isAnalyst(user) || isPetasenseAdmin(user);
}

// Mirrors the backend's User.can_access_watchlist() (analyst or Petasense
// admin). Deliberately narrower than the "full triage" view, which also
// includes Petasense viewers: they keep the urgency score and insights, but the
// dedicated /users/me/watchlist routes reject them outright and a
// watchlist-filtered machine list is refused, so they must not be offered the
// affordance. (Watchlist membership on list rows degrades quietly instead —
// is_watched comes back false rather than erroring.)
export function canAccessWatchlist(user) {
  return isAnalyst(user) || isPetasenseAdmin(user);
}

export function isOrgAdmin(user) {
  if (user && user.roles && user.roles.includes(userRoles.OrgAdmin)) return true;
  return false;
}

/** Org ids from UOA (`org_admin_organisation_ids` on current-user payload). */
export function getOrgAdminOrganisationIds(user) {
  if (!user || !Array.isArray(user.org_admin_organisation_ids)) return [];
  return user.org_admin_organisation_ids;
}

/** Org ids the user can reach, from the BE-scoped account switcher. */
export function getAccessibleOrgIds(user) {
  const items = user && user.account_switcher && Array.isArray(user.account_switcher.items)
    ? user.account_switcher.items
    : [];
  return items
    .filter(item => item.type === 'org-header' && item.organisation_id != null)
    .map(item => Number(item.organisation_id));
}

/**
 * Mirrors backend `_check_org_admin_access`.
 * Pass `orgId` when gating a specific organisation; omit to mean "any org dashboard".
 *
 * Petasense admins reach every org. Everyone else is scoped to the orgs they can
 * actually reach — the BE-scoped account switcher plus any explicit UOA org-admin
 * ids — rather than a blanket true for partner-admin / legacy org-admin, which
 * over-permitted relative to the backend (partner scoping / empty UOA window).
 */
export function canAccessOrgDashboard(user, orgId) {
  if (!user) return false;
  if (isPetasenseAdmin(user)) return true;

  const adminOrgIds = getOrgAdminOrganisationIds(user).map(Number);
  const isScopedAdmin = isPartnerAdmin(user) || isOrgAdmin(user) || adminOrgIds.length > 0;
  if (!isScopedAdmin) return false;

  const scopedIds = new Set([...getAccessibleOrgIds(user), ...adminOrgIds]);

  // No specific org (surfacing nav) → allow only if they can reach some org.
  if (orgId == null || orgId === '') return scopedIds.size > 0;
  return scopedIds.has(Number(orgId));
}

export function getHighestPrivilegeRole(roles) {
  const rolesList = [
    userRoles.PetasenseAdmin,
    userRoles.OrgAdmin,
    userRoles.Analyst,
    userRoles.Manager,
    userRoles.Technician,
    userRoles.Admin,
    userRoles.PetasenseViewer
  ];
  return rolesList.filter(r => roles.includes(r))[0];
}

export function checkPermissions(resource, permissions, operation, user) {
  if (isPetasenseAdmin(user) || isPartnerAdmin(user) || (isPetasenseViewer(user) && (operation === operations.Read))) return true;
  if (
    isOrgAdmin(user)
    && ORG_ADMIN_DASHBOARD_RESOURCES.includes(resource)
    && ORG_ADMIN_DASHBOARD_OPERATIONS.includes(operation)
  ) {
    return true;
  }
  if (!user || !permissions || !resource || !operation) return false;
  const userRoles = user.roles;
  for (let i = 0; i < userRoles.length; i++) {
    if (
      permissions[resource] &&
      permissions[resource][userRoles[i]] &&
      permissions[resource][userRoles[i]][operation]
    ) return true;
  }
  return false;
}

/**
 * Check if vibration agent feature is enabled for current user
 * Considers feature state: disabled, testing, or enabled
 */
export function isVibrationAgentEnabled(user, currentAccount) {
  // Petasense admins always have access (for testing)
  if (isPetasenseAdmin(user)) {
    return true;
  }

  // Check account feature state from Redux currentAccount
  if (currentAccount && currentAccount.preferences && currentAccount.preferences.enabled_features) {
    const state = currentAccount.preferences.enabled_features.vibration_agent;

    // Show UI only if state is "enabled" (not "testing" or "disabled")
    return state === 'enabled';
  }

  return false;
}

/**
 * Check if the FMEA feature should be visible for the current user.
 * Early-access gate: Petasense internal staff (admins and viewers) always see
 * FMEA so they can use and QA it before release. Customers see it only once the
 * account's `fmea` premium feature is fully enabled.
 */
export function isFmeaEnabled(user, currentAccount) {
  // Petasense internal staff (admins and viewers) always have access.
  if (isPetasenseAdmin(user) || isPetasenseViewer(user)) {
    return true;
  }

  // Otherwise require the account's FMEA feature to be fully enabled.
  if (currentAccount && currentAccount.preferences && currentAccount.preferences.enabled_features) {
    return currentAccount.preferences.enabled_features.fmea === 'enabled';
  }

  return false;
}
